import { IReportStandardQuery, reportOgqlQuery, reportOgqlV1Query, reportStandardQuery } from '@opengov/api-deprecated';
import { ogqlEffects } from '@opengov/ogql-client/effects';
import { ogqlClientActionCreators } from '@opengov/ogql-client/state';
import { EOgqlJobState, IOgqlJobResult, IOgqlV1JobResult } from '@opengov/ogql-types';
import { EQueryType, IConditionalReportColumn, IReportConfiguration } from '@opengov/reporting-types';
import { IInfixReportColumn } from '@opengov/reporting-types/src/reportColumns-immer';
import { produce } from 'immer';
import { memoize } from 'lodash';

let ident = -200000;
export const uniqueId = () => {
  ident = ident + 1;
  return `${ident}`;
};

const v0StandardQuery = async (params: IReportStandardQuery) => reportStandardQuery<IOgqlJobResult>(params);
const v1StandardQuery = async (params: IReportStandardQuery) => reportStandardQuery<IOgqlV1JobResult>(params);

const runMemoizedQuery = memoize(reportOgqlQuery, (params) => JSON.stringify(params));
const runMemoizedV1Query = memoize(reportOgqlV1Query, (params) => JSON.stringify(params));
const runMemoizedV0StandardQuery = memoize(v0StandardQuery, (params) => JSON.stringify(params));
const runMemoizedV1StandardQuery = memoize(v1StandardQuery, (params) => JSON.stringify(params));

export const runV0Query = async (e: {
  body:
    | IReportConfiguration
    | { reportConfiguration: IReportConfiguration }
    | { column: IInfixReportColumn | IConditionalReportColumn<string | number | Date>; stringToSearchFor?: string };
  childParentKey?: string;
  isStandardTemplate?: boolean;
  lensId?: string;
  limit?: number;
  offset?: number;
  // Query id is an optional, arbitrary value to grant some control over query memoization.
  queryId?: string;
  queryType: EQueryType;
  reportId?: string;
  templateId?: string;
  useLargeScaleQueryLimit?: boolean;
  useQueryService?: boolean;
}): Promise<IOgqlJobResult> => {
  const {
    body,
    childParentKey,
    isStandardTemplate = false,
    lensId,
    limit,
    offset,
    queryId = '',
    queryType,
    reportId,
    templateId,
    useLargeScaleQueryLimit,
    useQueryService
  } = e;

  const uId = uniqueId();

  ogqlClientActionCreators.queueQuery({ queryString: '', queryType, id: uId, queryParams: { childParentKey } });

  const queryPromise = isStandardTemplate
    ? runMemoizedV0StandardQuery({
        templateId,
        queryType,
        body: { ...body, lensId },
        useLargeScaleQueryLimit,
        useQueryService,
        offset,
        limit
      })
    : runMemoizedQuery({
        reportId,
        queryId,
        queryType,
        body,
        useLargeScaleQueryLimit,
        useQueryService,
        offset,
        limit
      });

  return queryPromise
    .then(async (result) => {
      const isCancelled = await ogqlEffects.isCancelled(uId);

      if (isCancelled) {
        return {
          id: uId,
          state: EOgqlJobState.cancelled
        };
      }
      return result;
    })
    .then((result) => {
      ogqlClientActionCreators.queryFinished({ id: uId } as any);
      return result;
    })
    .catch((err) => {
      ogqlClientActionCreators.queryFailed({ id: uId } as any);
      throw err;
    });
};

interface IQueryExecutor {
  (bodyWithoutId: IReportConfiguration, params: IRunV1Query): Promise<IOgqlV1JobResult>;
}
export interface IRunV1Query {
  queryType: EQueryType;
  reportConfiguration: IReportConfiguration;
  useLargeScaleQueryLimit?: boolean;
  useQueryService?: boolean;
  offset?: number;
  limit?: number;
  isStandardTemplate?: boolean;
  allowQueryExecution?: boolean;
  lensId?: string;
  templateId?: string;
  dimensionalSummaryQueryIndex?: number;
  childParentKey?: string;
  executeQuery?: IQueryExecutor;
}

export const runV1Query = async (params: IRunV1Query): Promise<IOgqlV1JobResult> => {
  const {
    queryType,
    reportConfiguration,
    dimensionalSummaryQueryIndex,
    allowQueryExecution,
    executeQuery = defaultExecuteQuery
  } = params;
  // We should remove the id so we can memoize our initial data fetches
  const bodyWithoutId = createMemoizableReportConfig(reportConfiguration);
  const uId = uniqueId();

  ogqlClientActionCreators.queueQuery({
    queryString: '',
    queryParams: { dimensionalSummaryQueryIndex, allowQueryExecution },
    queryType,
    id: uId
  });

  return executeQuery(bodyWithoutId, params)
    .then(async (result) => {
      const isCancelled = await ogqlEffects.isCancelled(uId);
      if (isCancelled) {
        // cancelled means it's not in the list anymore
        // no ogqlClientActionCreators thing to call at this point
        return {
          id: uId,
          state: EOgqlJobState.cancelled
        };
      }

      return result;
    })
    .then((result) => {
      ogqlClientActionCreators.queryFinished({ id: uId, queryString: '' });
      return result;
    })
    .catch((err) => {
      ogqlClientActionCreators.queryFailed({ id: uId, queryString: '' });
      throw err;
    });
};

const resetQueryCache = () => {
  runMemoizedQuery.cache.clear();
  runMemoizedV1Query.cache.clear();
  runMemoizedV0StandardQuery.cache.clear();
  runMemoizedV1StandardQuery.cache.clear();
};

// We should expire the cache after an hour
setInterval(resetQueryCache, 60 * 60 * 1000);

function defaultExecuteQuery(bodyWithoutId: IReportConfiguration, params: IRunV1Query): Promise<IOgqlV1JobResult> {
  let queryPromise: any;
  const {
    templateId,
    queryType,
    dimensionalSummaryQueryIndex,
    offset,
    limit,
    isStandardTemplate = false,
    useLargeScaleQueryLimit = false,
    useQueryService = false
  } = params;
  if (isStandardTemplate) {
    queryPromise = runMemoizedV1StandardQuery({
      templateId,
      queryType,
      body: bodyWithoutId,
      useLargeScaleQueryLimit,
      useQueryService,
      offset,
      limit
    });
  } else {
    queryPromise = runMemoizedV1Query({
      queryType,
      reportConfiguration: bodyWithoutId,
      useLargeScaleQueryLimit,
      useQueryService,
      offset,
      limit,
      dimensionalSummaryQueryIndex
    });
  }
  return queryPromise;
}

function createMemoizableReportConfig(reportConfiguration: IReportConfiguration) {
  let bodyWithoutId = reportConfiguration;
  if (reportConfiguration.id) {
    bodyWithoutId = produce(reportConfiguration, (newReportConfiguration) => {
      newReportConfiguration.id = null;
    });
  }
  return bodyWithoutId;
}
